/**
 * SoleadLab 2.0 — Monitoring Dashboard Page
 *
 * Two views via tabs:
 *   "Overview" — KPI cards + Power Flow + Alerts + Chart + Site card
 *   "Panel Grid" — TreePanel + TileGrid + InspectorDrawer
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMonitoringStore } from '../stores';
import KpiCard from '../components/monitoring/KpiCard';
import PowerFlowDiagram from '../components/monitoring/PowerFlowDiagram';
import AlertsList, { type AlertItem } from '../components/monitoring/AlertsList';
import EnergyChart from '../components/monitoring/EnergyChart';
import TileGrid, { type TileData } from '../components/monitoring/TileGrid';
import TreePanel from '../components/monitoring/TreePanel';
import InspectorDrawer, { getMockInspectorData, type InspectorData } from '../components/monitoring/InspectorDrawer';
import SiteCard from '../components/monitoring/SiteCard';
import './MonitoringPage.css';

// ─── SVG icons ────────────────────────────────────────────────────────────────

function IconBack() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  );
}

function IconGrid() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

function IconOverview() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="8" height="5" rx="1"/>
      <rect x="13" y="3" width="8" height="5" rx="1"/>
      <rect x="3" y="10" width="18" height="11" rx="1"/>
    </svg>
  );
}

function IconSun() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type View = 'overview' | 'panel-grid';

export default function MonitoringPage() {
  const [view, setView] = useState<View>('overview');
  const [inspector, setInspector] = useState<InspectorData | null>(null);

  // Real monitoring store data
  const isStreaming = useMonitoringStore((s) => s.isStreaming);
  const streamError = useMonitoringStore((s) => s.streamError);
  const lastRefreshAt = useMonitoringStore((s) => s.lastRefreshAt);
  const providerStatus = useMonitoringStore((s) => s.providerStatus);

  const isConnected = isStreaming || providerStatus === 'connected';

  const formattedTime = lastRefreshAt
    ? `Updated ${new Date(lastRefreshAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : 'Updated --:--';

  const handleTileSelect = (tile: TileData | null) => {
    setInspector(tile ? getMockInspectorData(tile.id) : null);
  };

  const handleAlertClick = (alert: AlertItem) => {
    setInspector({
      id: alert.id,
      label: alert.source,
      status: alert.severity === 'critical' ? 'fault' : alert.severity === 'warning' ? 'warning' : 'active',
      meta: { lastSeen: alert.timestamp },
    });
  };

  return (
    <div className="mon-page">
      {/* ── Sidebar ── */}
      <nav className="mon-sidebar">
        <div className="mon-sidebar__logo">
          <div className="mon-sidebar__dot" />
        </div>

        <div className="mon-sidebar__items">
          {/* Back to editor */}
          <Link
            to="/editor"
            className="mon-nav-item"
            title="Back to Editor"
          >
            <IconBack />
          </Link>
        </div>

        <div className="mon-sidebar__bottom">
          {/* Sun icon — placeholder */}
          <div className="mon-nav-item mon-nav-item--passive" title="Live data active">
            <IconSun />
          </div>
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className="mon-main">
        {/* ── Page header ── */}
        <header className="mon-header">
          <div className="mon-header__left">
            <h1 className="mon-header__title">Monitoring Dashboard</h1>
            <span className={`mon-header__live ${isConnected ? '' : 'mon-header__live--off'}`}>
              {isConnected ? '● Live' : '○ Offline'}
            </span>
          </div>

          {/* View tabs */}
          <div className="mon-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'overview'}
              className={`mon-tab${view === 'overview' ? ' mon-tab--active' : ''}`}
              onClick={() => setView('overview')}
            >
              <IconOverview />
              Overview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'panel-grid'}
              className={`mon-tab${view === 'panel-grid' ? ' mon-tab--active' : ''}`}
              onClick={() => setView('panel-grid')}
            >
              <IconGrid />
              Panel Grid
            </button>
          </div>

          {/* Timestamp */}
          <div className="mon-header__right">
            <span className="mon-header__time">{formattedTime}</span>
          </div>
        </header>

        {/* Disconnected banner */}
        {streamError && (
          <div className="mon-banner mon-banner--error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span>Connection lost: {streamError}</span>
          </div>
        )}

        {/* ── Overview view ── */}
        {view === 'overview' && (
          <div className="mon-overview">
            {/* Top row: KPI cards */}
            <div className="mon-kpi-row">
              <KpiCard
                title="Solar Production"
                value={45.2}
                unit="kW"
                min={12.1}
                max={56.8}
                status="active"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>}
              />
              <KpiCard
                title="Today's Generation"
                value={312.4}
                unit="kWh"
                min={0}
                max={560}
                status="active"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
              />
              <KpiCard
                title="CO₂ Avoided"
                value={187.4}
                unit="kg"
                status="active"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22V12M12 12C12 6.477 7.523 2 2 2"/><path d="M12 12C12 6.477 16.477 2 22 2"/><path d="M2 22h20"/></svg>}
              />
              <KpiCard
                title="System Efficiency"
                value={94.3}
                unit="%"
                status="warning"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
              />
            </div>

            {/* Middle row: Power Flow + Alerts */}
            <div className="mon-mid-row">
              <div className="mon-mid-row__flow">
                <PowerFlowDiagram />
              </div>
              <div className="mon-mid-row__alerts">
                <AlertsList onAlertClick={handleAlertClick} />
              </div>
            </div>

            {/* Bottom row: Chart + Site card */}
            <div className="mon-bottom-row">
              <div className="mon-bottom-row__chart">
                <EnergyChart />
              </div>
              <div className="mon-bottom-row__site">
                <SiteCard />
              </div>
            </div>
          </div>
        )}

        {/* ── Panel Grid view ── */}
        {view === 'panel-grid' && (
          <div className="mon-panel-grid">
            {/* Left tree */}
            <div className="mon-panel-grid__tree">
              <TreePanel onSelect={(id) => {
                if (id) setInspector(getMockInspectorData(id));
                else setInspector(null);
              }} />
            </div>

            {/* Center tile grid */}
            <div className="mon-panel-grid__tiles">
              <div className="mon-tiles-header">
                <h2 className="mon-tiles-title">String Output</h2>
                <span className="mon-tiles-meta">24 strings · 2 faults · 4 idle</span>
              </div>
              <div className="mon-tiles-body card">
                <TileGrid onTileSelect={handleTileSelect} />
              </div>
            </div>

            {/* Right inspector (inline, not overlay) */}
            <div className="mon-panel-grid__inspector">
              {inspector ? (
                <div className="insp-inline card">
                  {/* Header */}
                  <div className="insp-inline__header">
                    <div>
                      <h2 className="insp-inline__title">{inspector.label}</h2>
                      <span className={`insp-badge insp-badge--${inspector.status}`}>
                        <span className="insp-badge__dot" />
                        {inspector.status}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="insp-close"
                      onClick={() => setInspector(null)}
                      aria-label="Close inspector"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                  {/* Electrical */}
                  {inspector.electrical && (
                    <section className="insp-section">
                      <h3 className="insp-section__title">Electrical</h3>
                      {inspector.electrical.power !== undefined && <div className="insp-field"><span className="insp-field__label">Power</span><span className="insp-field__value tabular">{inspector.electrical.power.toFixed(2)} kW</span></div>}
                      {inspector.electrical.current !== undefined && <div className="insp-field"><span className="insp-field__label">Current</span><span className="insp-field__value tabular">{inspector.electrical.current.toFixed(1)} A</span></div>}
                      {inspector.electrical.voltage !== undefined && <div className="insp-field"><span className="insp-field__label">Voltage</span><span className="insp-field__value tabular">{inspector.electrical.voltage.toFixed(1)} V</span></div>}
                    </section>
                  )}
                  {inspector.physical && (
                    <section className="insp-section">
                      <h3 className="insp-section__title">Physical</h3>
                      {inspector.physical.tilt !== undefined && <div className="insp-field"><span className="insp-field__label">Tilt</span><span className="insp-field__value">{inspector.physical.tilt}°</span></div>}
                      {inspector.physical.azimuth !== undefined && <div className="insp-field"><span className="insp-field__label">Azimuth</span><span className="insp-field__value">{inspector.physical.azimuth}°</span></div>}
                    </section>
                  )}
                  {inspector.meta && (
                    <section className="insp-section">
                      <h3 className="insp-section__title">Info</h3>
                      {inspector.meta.lastSeen && <div className="insp-field"><span className="insp-field__label">Last seen</span><span className="insp-field__value">{inspector.meta.lastSeen}</span></div>}
                      {inspector.meta.serialNo && <div className="insp-field"><span className="insp-field__label">Serial No.</span><span className="insp-field__value">{inspector.meta.serialNo}</span></div>}
                    </section>
                  )}
                </div>
              ) : (
                <div className="insp-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="1.5">
                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                    <path d="M10 10l4 4M14 10l-4 4"/>
                  </svg>
                  <p>Select a string or inverter to inspect</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Inspector drawer (used in Overview on alert click) */}
      {view === 'overview' && (
        <InspectorDrawer
          data={inspector}
          onClose={() => setInspector(null)}
        />
      )}
    </div>
  );
}
